import { useState, useEffect, useCallback, useMemo, useImperativeHandle, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ShopSelector from '@/components/selectors/ShopSelector';
import DateRangeSelector from '@/components/selectors/DateRangeSelector';
import { useShopPersistence } from '@/components/shared/navigation/securePage';
import { FilterRef } from '@features/statistics/types.ts';

export interface FilterShopDateRangeData {
    shopId: string;
    start: string;
    end: string;
}

export interface FilterShopDateRangeProps {
    range?: number;
    maxRange?: number;
    onLoadData: (data: FilterShopDateRangeData) => void;
    ref?: Ref<FilterRef>;
}

const FilterShopDateRange = ({ range = 7, maxRange = 30, onLoadData, ref }: FilterShopDateRangeProps) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [startDate, setStartDate] = useState<DateTime>(DateTime.local().minus({ days: range }));
    const [endDate, setEndDate] = useState<DateTime>(DateTime.local());

    // Validation
    const errorMessages = useMemo(() => {
        const daysDiff = Math.abs(endDate.diff(startDate, 'days').days);
        return daysDiff <= maxRange
            ? []
            : [t('errors.rangeLtDays', { days: maxRange }) || `Range must be less than ${maxRange} days`];
    }, [startDate, endDate, maxRange, t]);

    const emitLoadData = useCallback(() => {
        if (!selectedShopIds[0]) return;

        const start = startDate.startOf('day').toISO();
        const end = endDate.endOf('day').toISO();

        if (!start || !end) return;

        const data: FilterShopDateRangeData = {
            shopId: selectedShopIds[0],
            start,
            end
        };

        onLoadData(data);
    }, [selectedShopIds, startDate, endDate, onLoadData]);

    useEffect(() => {
        if (!selectedShopIds[0] || errorMessages.length > 0) return;
        emitLoadData();
    }, [selectedShopIds[0], startDate, endDate]);

    useImperativeHandle(ref, () => ({ refresh: () => emitLoadData() }), [emitLoadData]);

    // Handle route query parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (params.get('shopId')) {
            setSelectedShopIds([params.get('shopId')!]);
        }

        if (params.get('start')) {
            const startParam = params.get('start');
            if (startParam) {
                const parsedStartDate = DateTime.fromISO(startParam, { zone: 'UTC' });
                if (parsedStartDate.isValid) {
                    setStartDate(parsedStartDate);
                }
            }
        }

        if (params.get('end')) {
            const endParam = params.get('end');
            if (endParam) {
                const parsedEndDate = DateTime.fromISO(endParam, { zone: 'UTC' });
                if (parsedEndDate.isValid) {
                    setEndDate(parsedEndDate);
                }
            }
        }
    }, [location.search]);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box
                className="filter-row"
                sx={{
                    display: 'flex',
                    gap: 3.5,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    mb: 5,
                    mt: 1
                }}
            >
                {/* Shop Selector */}
                <Box className="filter-field" sx={{ flex: '0 0 auto' }}>
                    <ShopSelector
                        value={selectedShopIds[0] ?? null}
                        onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                        label={t('shop')}
                    />
                </Box>

                {/* Date Range Selector */}
                <DateRangeSelector
                    startDate={startDate.toISODate() || ''}
                    endDate={endDate.toISODate() || ''}
                    onStartDateChange={(date: string) => setStartDate(DateTime.fromISO(date, { zone: 'UTC' }))}
                    onEndDateChange={(date: string) => setEndDate(DateTime.fromISO(date, { zone: 'UTC' }))}
                    maxRange={maxRange}
                    locale={i18n.language}
                    error={errorMessages.length > 0}
                    helperText={errorMessages.length > 0 ? errorMessages[0] : undefined}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default FilterShopDateRange;
